import {useReducer, useRef, useMemo, useCallback, useEffect, useState} from "react";
import {useDebounce} from "use-debounce";


import {useSearchParams} from "react-router-dom";
import type {TableRef} from "../components/base_table";
import type {
    SortingState,
    ColumnFiltersState,
    ColumnOrderState,
    RowSelectionState,
    PaginationState,
    ExpandedState,
} from "@tanstack/react-table";
import {
    getFilingPackTableColumns,
    mapStatusesToFilterOptions,
} from "../components/filing_packs_table/FilingPacksTableColumns";
import type {
    FilingPackTableColumn,
    StatusFilterOption,
} from "../components/filing_packs_table/FilingPacksTableColumns";
import {FilingPack, FilingPackEvent, FilingPackFilter} from "../shared/Types";
import {EventsTable} from "../components/events_table/EventsTable";
import {getEventsTableColumns} from "../components/events_table/EventsTableColumns";
import {overviewService} from "../shared/services/overviewServiceInstance";
import {getEnv} from "../shared/config/OktaConfig";
import {AWS_REGION} from "../shared/utils";

function FilingPackEventsLoader({ filingPack, onDownload, onShowPayload }: {
    filingPack: FilingPack;
    onDownload: (s3Reference: string) => void;
    onShowPayload: (s3Reference: string) => void;
}) {
    const eventsTableRef = useRef<TableRef>(null);
    const [events, setEvents] = useState<FilingPackEvent[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<any>(null);

    useEffect(() => {
        let cancelled = false;

        async function fetchEvents() {
            setIsLoading(true);
            setError(null);
            try {
                const result = await overviewService.findSystemEventsByFilingPackId(filingPack.id);
                if (!cancelled) {
                    setEvents(
                        (result.data.findSystemEventsByFilingPackId || [])
                            .filter((e: any): e is FilingPackEvent => !!e?.id)
                    );
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err);
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        }

        fetchEvents();
        return () => { cancelled = true; };
    }, [filingPack.id]);

    const columns = useCallback(() => getEventsTableColumns(onDownload, onShowPayload), [onDownload, onShowPayload]);

    return (
        <EventsTable
            tableRef={eventsTableRef}
            columns={columns}
            data={events}
            isLoading={isLoading}
            error={error}
        />
    );
}

type FilingPacksState = {
    loading: boolean;
    error: any;
    data: FilingPack[];
    sorting: SortingState;
    columnOrder: ColumnOrderState;
    columnFilters: ColumnFiltersState;
    rowSelection: RowSelectionState;
    expanded: ExpandedState;
    pagination: PaginationState & { totalCount?: number };
    globalSearch: string;
    columnFiltersSidePanelIsOpen: boolean;
};

type FilingPacksAction =
    | { type: "FETCH_START" }
    | { type: "FETCH_SUCCESS"; payload: { items: FilingPack[]; totalCount?: number } }
    | { type: "FETCH_ERROR"; payload: any }
    | { type: "SET_SORTING"; payload: SortingState }
    | { type: "SET_COLUMN_ORDER"; payload: ColumnOrderState }
    | { type: "SET_COLUMN_FILTERS"; payload: ColumnFiltersState }
    | { type: "TOGGLE_ROW"; payload: string | null }
    | { type: "SET_PAGINATION"; payload: PaginationState }
    | { type: "SET_EXPANDED"; payload: ExpandedState }
    | { type: "SET_GLOBAL_FILTER"; payload: string }
    | { type: "SET_COLUMN_FILTERS_SIDE_PANEL"; payload: boolean };

export function filingPacksReducer(state: FilingPacksState, action: FilingPacksAction): FilingPacksState {
    switch (action.type) {
        case "FETCH_START":
            return { ...state, loading: true, error: null };
        case "FETCH_SUCCESS":
            return {
                ...state,
                loading: false,
                error: null,
                data: action.payload.items,
                pagination: { ...state.pagination, totalCount: action.payload.totalCount },
            };
        case "FETCH_ERROR":
            return { ...state, loading: false, error: action.payload };
        case "SET_SORTING":
            return { ...state, sorting: action.payload };
        case "SET_COLUMN_ORDER":
            return { ...state, columnOrder: action.payload };
        case "SET_COLUMN_FILTERS":
            return {
                ...state,
                columnFilters: [...action.payload],
                pagination: { ...state.pagination, pageIndex: 0 },
            };
        case "TOGGLE_ROW": {
            const rowId = action.payload;
            if (!rowId || state.rowSelection[rowId]) {
                return { ...state, rowSelection: {}, expanded: {} };
            }
            return {
                ...state,
                rowSelection: { [rowId]: true },
                expanded: { [rowId]: true },
            };
        }
        case "SET_PAGINATION":
            return { ...state, pagination: { ...state.pagination, ...action.payload } };
        case "SET_GLOBAL_FILTER":
            return { ...state, globalSearch: action.payload };
        case "SET_EXPANDED":
            return { ...state, expanded: action.payload };
        case "SET_COLUMN_FILTERS_SIDE_PANEL":
            return { ...state, columnFiltersSidePanelIsOpen: action.payload };
        default:
            return state;
    }
}

/**
 * Convert column filters + global search to a FilingPackFilter for the AppSync query.
 */
export function buildQueryFilter(
    columnFilters: ColumnFiltersState,
    columns: FilingPackTableColumn<FilingPack>[],
    globalSearch?: string,
): FilingPackFilter | undefined {
    const filter: FilingPackFilter = {};

    columnFilters.forEach(({ id, value }) => {
        const col = columns.find(c => c.accessorKey === id);
        const filterKey = col?.filterKey || id;

        if (filterKey === 'status') {
            if (Array.isArray(value) && value.length > 0) {
                filter.status = value as string[];
            } else if (typeof value === 'string' && value !== '') {
                filter.status = [value];
            }
        } else if (filterKey === 'period' && typeof value === 'string') {
            filter.period = value;
        }
    });

    if (globalSearch) {
        filter.textSearch = globalSearch;
    }

    const hasValidFilter = Object.values(filter).some(
        v => v !== undefined && v !== null && v !== '',
    );

    return hasValidFilter ? filter : undefined;
}

export function useFilingPacksTable() {
    const tableRef = useRef<TableRef>(null);
    const [searchParams] = useSearchParams();
    const [statusFilterOptions, setStatusFilterOptions] = useState<StatusFilterOption[]>([]);

    useEffect(() => {
        let cancelled = false;

        async function fetchStatuses() {
            try {
                const result = await overviewService.listFilingPackStatuses();
                const statuses = (result.data.listFilingPackStatuses || [])
                    .filter((status: string | null): status is string => !!status);
                if (!cancelled) {
                    setStatusFilterOptions(mapStatusesToFilterOptions(statuses));
                }
            } catch (err) {
                console.error('Failed to load filing pack statuses:', err);
            }
        }

        fetchStatuses();
        return () => { cancelled = true; };
    }, []);

    const columns = useMemo(
        () => getFilingPackTableColumns(statusFilterOptions),
        [statusFilterOptions],
    );

    const rawPeriod = searchParams.get('period');
    const initialPeriod = rawPeriod && /^\d{6}$/.test(rawPeriod)
        ? `${rawPeriod.slice(0, 4)}-${rawPeriod.slice(4)}`
        : rawPeriod;

    const [state, dispatch] = useReducer(filingPacksReducer, initialPeriod, (period) => ({
        loading: false,
        error: null,
        data: [] as FilingPack[],
        sorting: [{ id: 'created_at', desc: true }] as SortingState,
        columnOrder: getFilingPackTableColumns().map(col => col.accessorKey as string),
        columnFilters: period
            ? [{ id: 'period_id', value: period }] as ColumnFiltersState
            : [] as ColumnFiltersState,
        rowSelection: {} as RowSelectionState,
        pagination: { pageIndex: 0, pageSize: 25, totalCount: undefined },
        globalSearch: '',
        expanded: {} as ExpandedState,
        columnFiltersSidePanelIsOpen: false,
    }));

    const queryFilter = useMemo(
        () => buildQueryFilter(state.columnFilters, columns, state.globalSearch),
        [state.columnFilters, columns, state.globalSearch],
    );

    const [debouncedFilter] = useDebounce(queryFilter, 1000);

    const { pageIndex, pageSize } = state.pagination;

    useEffect(() => {
        let cancelled = false;

        async function fetchData() {
            dispatch({ type: "FETCH_START" });
            try {
                const filter: FilingPackFilter = {
                    ...debouncedFilter,
                    offset: pageIndex * pageSize,
                    limit: pageSize,
                };
                const result = await overviewService.listFilingPacks(filter);
                const connection = result.data.listFilingPacks;
                const items = (connection?.items || [])
                    .filter((item: any): item is FilingPack => !!item?.id);
                if (!cancelled) {
                    dispatch({
                        type: "FETCH_SUCCESS",
                        payload: { items, totalCount: connection?.totalCount ?? items.length },
                    });
                }
            } catch (error) {
                if (!cancelled) {
                    dispatch({ type: "FETCH_ERROR", payload: error });
                }
            }
        }

        fetchData();

        return () => { cancelled = true; };
    }, [debouncedFilter, pageIndex, pageSize]);

    // Client-side sorting until the AppSync query supports sort parameters
    const sortedData = useMemo(() => {
        if (!state.sorting.length) return state.data;
        const { id, desc } = state.sorting[0];
        return [...state.data].sort((a, b) => {
            const aVal = (a as any)[id] ?? '';
            const bVal = (b as any)[id] ?? '';
            const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            return desc ? -cmp : cmp;
        });
    }, [state.data, state.sorting]);

    const [downloadError, setDownloadError] = useState<string | null>(null);
    const [payloadContent, setPayloadContent] = useState<string | null>(null);
    const [payloadLoading, setPayloadLoading] = useState(false);

    const dismissDownloadError = useCallback(() => setDownloadError(null), []);
    const dismissPayloadContent = useCallback(() => setPayloadContent(null), []);

    const handleDownload = useCallback(async (s3Reference: string) => {
        try {
            setDownloadError(null);
            const region = AWS_REGION;
            const bucket = getEnv('REACT_APP_S3_PAYLOAD_BUCKET') ?? '';
            const result = await overviewService.getPayloadUrl(region, bucket, s3Reference);
            const preSignedUrl = result.data.getPayloadUrlOnly?.preSignedUrl;
            if (preSignedUrl) {
                const response = await fetch(preSignedUrl);
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.setAttribute("download", s3Reference);
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }
        } catch (err: any) {
            const message = err?.message || String(err);
            console.error("Failed to download payload:", err);
            setDownloadError(`Failed to download payload: ${message}`);
        }
    }, []);

    const handleShowPayload = useCallback(async (s3Reference: string) => {
        try {
            setDownloadError(null);
            setPayloadLoading(true);
            const region = AWS_REGION;
            const bucket = getEnv('REACT_APP_S3_PAYLOAD_BUCKET') ?? '';
            const result = await overviewService.getPayloadUrl(region, bucket, s3Reference);
            const preSignedUrl = result.data.getPayloadUrlOnly?.preSignedUrl;
            if (preSignedUrl) {
                const response = await fetch(preSignedUrl);
                const text = await response.text();
                try {
                    const json = JSON.parse(text);
                    setPayloadContent(JSON.stringify(json, null, 2));
                } catch {
                    setPayloadContent(text);
                }
            }
        } catch (err: any) {
            const message = err?.message || String(err);
            console.error("Failed to fetch payload:", err);
            if (message.includes('PayloadTooLargeException')) {
                setDownloadError('The payload is too large for viewing. Please download it instead.');
            } else {
                setDownloadError(`Failed to fetch payload: ${message}`);
            }
        } finally {
            setPayloadLoading(false);
        }
    }, []);

    const renderExpandedContent = useCallback((filingPack: FilingPack) => {
        return <FilingPackEventsLoader filingPack={filingPack} onDownload={handleDownload} onShowPayload={handleShowPayload} />;
    }, [handleDownload, handleShowPayload]);

    const onSortChange = useCallback((newSorting: SortingState) =>
        dispatch({ type: "SET_SORTING", payload: newSorting }), []);

    const onColumnFiltersChange = useCallback((newFilters: ColumnFiltersState) =>
        dispatch({ type: "SET_COLUMN_FILTERS", payload: newFilters }), []);

    const onColumnOrderChange = useCallback((newOrder: ColumnOrderState) =>
        dispatch({ type: "SET_COLUMN_ORDER", payload: newOrder }), []);

    const onRowSelectionChange = useCallback((newRowSelection: RowSelectionState) => {
        const rowId = Object.keys(newRowSelection)[0] ?? null;
        dispatch({ type: "TOGGLE_ROW", payload: rowId });
    }, []);

    const onPaginationChange = useCallback((pagination: PaginationState) =>
        dispatch({ type: "SET_PAGINATION", payload: pagination }), []);

    const onGlobalFilterChange = useCallback((filter: string) =>
        dispatch({ type: "SET_GLOBAL_FILTER", payload: filter }), []);

    const onExpandedChange = useCallback((expanded: ExpandedState) =>
        dispatch({ type: "SET_EXPANDED", payload: expanded }), []);

    const onColumnFiltersSidePanelOpenChange = useCallback((isOpen: boolean) =>
        dispatch({ type: "SET_COLUMN_FILTERS_SIDE_PANEL", payload: isOpen }), []);

    return {
        tableRef,
        columns,
        data: state.loading ? [] : sortedData,
        isLoading: state.loading,
        error: state.error,
        downloadError,
        dismissDownloadError,
        payloadContent,
        payloadLoading,
        dismissPayloadContent,
        renderExpandedContent,

        sorting: state.sorting,
        columnFilters: state.columnFilters,
        columnOrder: state.columnOrder,
        rowSelection: state.rowSelection,
        expanded: state.expanded,
        globalSearch: state.globalSearch,
        columnFiltersSidePanelIsOpen: state.columnFiltersSidePanelIsOpen,
        pageIndex: state.pagination.pageIndex,
        pageSize: state.pagination.pageSize,
        totalCount: state.pagination.totalCount,

        onSortChange,
        onColumnFiltersChange,
        onColumnOrderChange,
        onRowSelectionChange,
        onPaginationChange,
        onGlobalFilterChange,
        onExpandedChange,
        onColumnFiltersSidePanelOpenChange,
    };
}
